import { useState, useEffect } from 'react';
import { ALL_STATES } from '../data/states';

interface IpApiResponse {
  state?: string | null;
}

// When the visitor is outside the US (or detection fails), default to a
// random state rather than always California. Picked once per session so
// every consumer sees the same default.
const randomFallbackState = (): string =>
  ALL_STATES[Math.floor(Math.random() * ALL_STATES.length)]?.code ?? 'CA';

const fallbackState = randomFallbackState();

// Session-level cache: the user's state doesn't change between mounts, so
// detect once and reuse - avoids refiring the request on every wizard open.
let cachedState: string | null = null;
let pendingDetection: Promise<string> | null = null;

const detectStateOnce = (): Promise<string> => {
  if (pendingDetection) return pendingDetection;
  pendingDetection = (async () => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL;
      const response = await fetch(`${apiUrl || ''}/api/detect-state`);
      const data: IpApiResponse = await response.json();
      cachedState = data.state || fallbackState;
    } catch {
      cachedState = fallbackState;
    }
    return cachedState;
  })();
  return pendingDetection;
};

export function useIpState() {
  const [state, setState] = useState<string>(cachedState ?? fallbackState);

  useEffect(() => {
    if (cachedState) return;
    let mounted = true;
    detectStateOnce().then((detected) => {
      if (mounted) setState(detected);
    });
    return () => {
      mounted = false;
    };
  }, []);

  return state;
}
